import React, { useState, useCallback } from 'react'
import { DeliveryInfoInput } from 'src/apollo/onlineOrdering'
import {
  getCountryAddressConfig,
  getMissingRequiredFieldErrors
} from 'src/public/components/online_ordering/address_configs'
import useGeocoder from '../../../../../shared/components/common/location_search/useGeocoder'
import { FormProvider, useForm } from 'react-hook-form'
import InputField from 'src/shared/components/common/form_input/InputField'
import { ButtonType } from 'src/apollo/sites'
import { BrandedButton } from 'src/shared/components/common/button'
import { useRestaurant } from '../../../../../shared/components/common/restaurant_context/RestaurantContext'
import { ScreenProps } from 'src/public/components/default_template/online_ordering/delivery_address_modal/DeliveryModal'
import {
  getInvalidTrackingSource,
  trackInvalidDeliveryAddressEntered,
  trackValidDeliveryAddressEntered
} from 'src/public/components/default_template/online_ordering/dining_behavior_toggle/fulfillmentTrackingUtils'
import tracker from 'src/lib/js/tracker'
import { useCart } from 'src/public/components/online_ordering/CartContext'
import LoadingSpinnerOverlay from '../../../../../shared/components/common/loading_spinner/LoadingSpinnerOverlay'
import { useDelivery } from 'src/public/components/online_ordering/DeliveryContext'
import { ArrowBackIcon, WarningOutlineIcon } from '@toasttab/buffet-pui-icons'
import { t } from 'src/translations/src'
import { dummyAddressValue } from 'src/public/components/online_ordering/addressUtils'
import { TrackingEvents } from 'shared/components/common/tracking/TrackingEvents'

const ManualAddressScreen: React.FC<ScreenProps> = ({
  goToScreen,
  deliveryAddress,
  trackingSource,
  partialDeliveryAddress,
  previousSearchText
}) => {
  const { selectedLocation, i18n } = useRestaurant()
  const { cart } = useCart()
  const geocoder = useGeocoder()
  const { isAddressInRange } = useDelivery()
  const countryAddressConfig = getCountryAddressConfig(i18n?.country)
  const [loading, setLoading] = useState(false)
  // partialDeliveryAddress (last searched/entered) > deliveryAddress (confirmed)
  const initialAddress = partialDeliveryAddress ?? deliveryAddress
  const addressCountry = i18n?.country ?? initialAddress?.country ?? ''

  const formMethods = useForm({
    mode: 'onChange',
    defaultValues: {
      manualAddress1: initialAddress?.address1 ?? '',
      manualAddress2: initialAddress?.address2 ?? '',
      manualCity: initialAddress?.city ?? '',
      manualState: initialAddress?.state ?? '',
      manualAdministrativeArea: initialAddress?.administrativeArea ?? '',
      manualZipCode:
        initialAddress?.zipCode && initialAddress?.zipCode !== dummyAddressValue
          ? initialAddress?.zipCode
          : ''
    }
  })
  const [validationError, setValidationError] = useState<string>('')
  const { getValues } = formMethods

  const backNavHandler = useCallback(() => {
    // Go back to search screen, preserving address or search text if available
    goToScreen(
      'address-search',
      deliveryAddress ?? null,
      undefined,
      partialDeliveryAddress,
      previousSearchText
    )
  }, [deliveryAddress, goToScreen, partialDeliveryAddress, previousSearchText])

  const handleError = () =>
    setValidationError(t('address-search-screen.trouble-verifying-address'))

  const isPreciseLocation = useCallback(
    (result: google.maps.GeocoderResult) => {
      const precision = countryAddressConfig.addressPrecision

      if (precision === 'street') {
        // Accept only street level matches
        return result.geometry.location_type !== 'APPROXIMATE'
      }

      // Accept street or postal code level matches
      const viewport = result.geometry.viewport
      if (viewport) {
        const northeast = viewport.getNorthEast()
        const southwest = viewport.getSouthWest()

        const latDelta = Math.abs(northeast.lat() - southwest.lat())
        const lngDelta = Math.abs(northeast.lng() - southwest.lng())

        if (latDelta > 0.01 || lngDelta > 0.01) return false
      }

      return true
    },
    [countryAddressConfig]
  )

  const handleSubmit = useCallback(async () => {
    const addressValues = getValues()

    // Geocode without address2 (apt/unit) and dummy values, they confuse Google
    const addressParts = [
      addressValues.manualAddress1,
      addressValues.manualCity,
      addressValues.manualZipCode !== dummyAddressValue
        ? addressValues.manualZipCode
        : null,
      addressCountry
    ]
      .filter(Boolean)
      .join(', ')

    geocoder().geocode(
      {
        address: addressParts,
        componentRestrictions: addressCountry
          ? { country: addressCountry }
          : undefined
      },
      async (results, status) => {
        if (status === 'OK' && results?.[0]?.geometry) {
          const result = results[0]

          const completeAddress: DeliveryInfoInput = {
            address1: addressValues.manualAddress1,
            address2: addressValues.manualAddress2 || undefined,
            city: addressValues.manualCity,
            state: addressValues.manualState || dummyAddressValue,
            administrativeArea: addressValues.manualAdministrativeArea || '',
            zipCode: addressValues.manualZipCode,
            latitude: results[0].geometry.location.lat(),
            longitude: results[0].geometry.location.lng()
          }

          const missingFields = getMissingRequiredFieldErrors(
            completeAddress,
            addressCountry
          )
          if (missingFields.length > 0) {
            return setValidationError(
              // if there are multiple errors, just show the first one
              t(missingFields[0])
            )
          }

          if (!isPreciseLocation(result)) {
            tracker.track(TrackingEvents.MANUAL_ADDRESS_LOCATION_MATCH_ERROR, {
              num_items_in_cart: cart?.order?.selections?.length || 0,
              saved_address: false,
              source: trackingSource
            })

            return handleError()
          }

          tracker.track(TrackingEvents.ENTERED_MANUAL_DELIVERY_ADDRESS, {
            num_items_in_cart: cart?.order?.selections?.length || 0,
            saved_address: false,
            source: trackingSource
          })

          if (cart?.restaurant || selectedLocation?.externalId) {
            try {
              setLoading(true)
              const inRange = await isAddressInRange(completeAddress)
              setLoading(false)
              if (inRange) {
                goToScreen('address-confirm', completeAddress)
                trackValidDeliveryAddressEntered(
                  tracker,
                  cart?.order?.selections?.length || 0,
                  false,
                  trackingSource
                )
              } else {
                trackInvalidDeliveryAddressEntered(
                  tracker,
                  cart?.order?.selections?.length || 0,
                  false,
                  trackingSource
                )
                goToScreen(
                  'address-invalid',
                  completeAddress,
                  getInvalidTrackingSource(trackingSource)
                )
              }
            } catch (_) {
              handleError()
              setLoading(false)
            }
          } else {
            trackInvalidDeliveryAddressEntered(
              tracker,
              cart?.order?.selections?.length || 0,
              false,
              trackingSource
            )
            goToScreen(
              'address-invalid',
              null,
              getInvalidTrackingSource(trackingSource)
            )
          }
        } else {
          return handleError()
        }
      }
    )
  }, [
    getValues,
    geocoder,
    cart?.order?.selections?.length,
    cart?.restaurant,
    trackingSource,
    selectedLocation?.externalId,
    deliveryAddress,
    isAddressInRange,
    goToScreen
  ])

  return (
    <div className='manualAddressScreen'>
      <div className='modalHeader'>
        <button
          data-testid='address-search-back-btn'
          aria-label={t('fulfillment-general.back')}
          type='button'
          className='backArrow'
          onClick={backNavHandler}
        >
          <ArrowBackIcon />
        </button>
        <h2 id='delivery-address-modal-title' tabIndex={-1}>
          {t('address-search-screen.title')}
        </h2>
      </div>
      {validationError && (
        <div className='warningBanner' data-testid='address-search-error'>
          <WarningOutlineIcon size='xs' accessibility='decorative' />
          <span>{validationError}</span>
        </div>
      )}
      <div className='modalContent'>
        <div className='manualAddressContainer'>
          {partialDeliveryAddress ? (
            <p className='helperText'>{t('manual-address-screen.helper')}</p>
          ) : (
            <h3>{t('manual-address-screen.your-address')}</h3>
          )}
          <FormProvider {...formMethods}>
            <form>
              <InputField
                id='manualAddress1'
                type='text'
                label={t(countryAddressConfig.fields.address1.labelKey)}
                preventFormSubmit
              />
              {countryAddressConfig.fields.address2 && (
                <InputField
                  id='manualAddress2'
                  type='text'
                  label={t(countryAddressConfig.fields.address2.labelKey)}
                  preventFormSubmit
                />
              )}
              <InputField
                id='manualCity'
                type='text'
                label={t(countryAddressConfig.fields.city.labelKey)}
                preventFormSubmit
              />
              {countryAddressConfig.fields.stateOrProvince && (
                <InputField
                  id={`${
                    addressCountry === 'US'
                      ? 'manualState'
                      : 'manualAdministrativeArea'
                  }`}
                  type='text'
                  label={t(
                    countryAddressConfig.fields.stateOrProvince.labelKey
                  )}
                  preventFormSubmit
                />
              )}
              <InputField
                id='manualZipCode'
                type='text'
                label={t(countryAddressConfig.fields.postalCode.labelKey)}
                preventFormSubmit
              />
            </form>
          </FormProvider>
        </div>
      </div>
      <div className='footer'>
        <BrandedButton
          variant={ButtonType.Secondary}
          className='cancelButton'
          onClick={backNavHandler}
        >
          {t('manual-address-screen.cancel')}
        </BrandedButton>
        <BrandedButton
          variant={ButtonType.Primary}
          className='manualAddressSubmitButton'
          onClick={handleSubmit}
        >
          {t('manual-address-screen.continue')}
        </BrandedButton>
      </div>
      {loading && <LoadingSpinnerOverlay withSubtleBorderRadius />}
    </div>
  )
}

export default ManualAddressScreen
